import { call, put, takeLatest, select } from 'redux-saga/effects';
import { get } from 'lodash';
import { message, notification } from 'antd';
import { FORM_TYPES } from './types';
import * as actions from './actions';
import { formViewSelector, policyLookupSelector } from './selectors';
import { updateSchedule, deleteSchedule } from '@api/schedulePolicies';
import { isScheduleViewSelector } from '../precisionPolicy/selectors';
import * as policyActions from '../precisionPolicy/actions';
import { genSummaryText, messageMap, policyAction } from './utils';

const displayError = (err) => {
  const error = get(err, 'response.data.message', 'Unknown Error');
  message.error(error);
};

function* generateUpdateSummary(out) {
  // duration set to 0 means the notification
  // will not be closed automatically
  // useful for error/partial case
  const { level, text, duration = 0 } = messageMap[out.status];
  const policyLookup = yield select(policyLookupSelector);

  const description = genSummaryText(policyLookup, out);
  const config = {
    message: text,
    description: description.join(','),
    duration,
  };
  notification[level](config);
}

function* init() {
  yield put(actions.initForm());
  yield put(actions.initList());
}

function* updateScheduleForActivate(saveType) {
  const { activationState, notes, policies, scheduleId, triggerAt, emailIds } =
    yield select(formViewSelector);

  const res = yield call(updateSchedule, {
    data: {
      activationState,
      notes,
      emailIds,
      policies,
      scheduleId,
      triggerAt,
      type: saveType,
    },
  });

  if (activationState === 'now') {
    yield generateUpdateSummary(res.data);
  } else {
    notification.success({
      message: `Schedule ${scheduleId ? 'updated' : 'saved'} successfully.`,
    });
  }
}

function* updateScheduleForReorder(saveType) {
  const { notes, scheduleId, triggerAt, emailIds, order } = yield select(formViewSelector);

  const res = yield call(updateSchedule, {
    data: {
      activationState: 'schedule',
      notes,
      emailIds,
      order,
      scheduleId,
      triggerAt,
      type: saveType,
    },
  });

  notification.success({
    message: `Schedule ${scheduleId ? 'updated' : 'saved'} successfully.`,
  });

  return res;
}

function* saveRecord(action) {
  yield put(actions.setFormLoading(true));
  const isScheduleView = yield select(isScheduleViewSelector);
  try {
    const { saveType } = action;

    if (saveType === policyAction.activate) {
      yield updateScheduleForActivate(saveType);
    } else if (saveType === policyAction.reorder) {
      yield updateScheduleForReorder(saveType);
    }

    // done, close and reset form
    yield call(action.doneCallback);

    // reload list/calendar view
    if (!isScheduleView) {
      yield put(policyActions.init());
    }
  } catch (err) {
    displayError(err);
    // allow user to try saving again
    yield put(actions.setFormLoading(false));
  }
}

function* deleteRecord(action) {
  yield put(actions.setFormLoading(true));
  const { scheduleId } = yield select(formViewSelector);
  try {
    // TODO: check response and update message
    yield call(deleteSchedule, scheduleId);
    // done, close and reset form
    yield call(action.doneCallback);
    message.success(`Schedule deleted successfully.`);
  } catch (err) {
    displayError(err);
    yield put(actions.setFormLoading(false));
  }
}

const watchMap = {
  [FORM_TYPES.SAVE_SCHEDULE]: saveRecord,
  [FORM_TYPES.DELETE_SCHEDULE]: deleteRecord,
};

function* watch() {
  yield init();
  for (const [type, func] of Object.entries(watchMap)) {
    yield takeLatest([type], func);
  }
}

export default [watch];
